// src/components/multiselect/TarjetasPorClienteMultiSelect.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import apiRoutes from "../../api/apiRoutes";
import { apiFetchJson } from "../../utils/http";

const PAGE_SIZE = 100;
const ESTADO_TARJETA_ACTIVA_ID = 29;

export default function TarjetasPorClienteMultiSelect({
  clienteId = null,      // requerido si sos ADMIN
  value = [],
  onChange = () => {},
  disabled = false,
}) {
  const ref = useRef(null);

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // =========================
  // CARGA TARJETAS DEL CLIENTE
  // =========================
  useEffect(() => {
    let cancelled = false;

    // sin cliente seleccionado → limpiar
    if (!clienteId) {
      setItems([]);
      onChange([]);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        let page = 0;
        let hasMore = true;
        const acc = [];

        while (hasMore) {
          const url = apiRoutes.tarjetas.autocompleteUrl(
            "numero",
            "",
            page,
            PAGE_SIZE,
            ESTADO_TARJETA_ACTIVA_ID
            ) + `&clienteId=${clienteId}`;

          const data = await apiFetchJson(url);

          const rows = data?.items ?? [];
          acc.push(
            ...rows.map((r) => ({
              id: r.id,
              label: r.numero,
            }))
          );

          hasMore = Boolean(data?.hasMore);
          page++;
        }

        if (!cancelled) {
          setItems(acc);
        }
      } catch (e) {
        console.error("Tarjetas:", e);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  // =========================
  // CLICK FUERA
  // =========================
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // =========================
  // FILTRADO LOCAL
  // =========================
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return items;
    return items.filter((i) =>
      `${i.label}`.toLowerCase().includes(q)
    );
  }, [items, search]);

  // =========================
  // LABEL BOTÓN
  // =========================
  const label =
    value.length === 0
      ? "Seleccionar"
      : value.length === 1
      ? "1 tarjeta"
      : `${value.length} tarjetas`;

  // =========================
  // HANDLERS
  // =========================
  const toggle = (id) =>
    onChange(
      value.includes(id)
        ? value.filter((x) => x !== id)
        : [...value, id]
    );

  const marcarTodas = () => onChange(filtered.map((f) => f.id));
  const desmarcarTodas = () =>
    onChange(value.filter((id) => !filtered.some((f) => f.id === id)));

  // =========================
  // RENDER
  // =========================
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled || !clienteId}
        className="input w-full flex items-center text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="truncate">
          {loading ? "Cargando tarjetas..." : label}
        </span>
        <span className="ml-auto">▾</span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl">
          {/* HEADER */}
          <div className="p-2 border-b bg-gray-50 flex flex-wrap gap-2">
            <input
              placeholder="Filtrar tarjeta"
              className="flex-1 border rounded px-2 py-1"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              type="button"
              onClick={marcarTodas}
              className="text-green-700 text-sm"
            >
              ✓ Marcar todas
            </button>
            <button
              type="button"
              onClick={desmarcarTodas}
              className="text-green-700 text-sm"
            >
              ✗ Desmarcar todas
            </button>
          </div>

          {/* LISTA */}
          <div className="max-h-56 overflow-auto">
            {filtered.map((op) => (
              <label
                key={op.id}
                className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={value.includes(op.id)}
                  onChange={() => toggle(op.id)}
                />
                <span className="truncate">{op.label}</span>
              </label>
            ))}

            {!loading && filtered.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500">
                Sin tarjetas
              </div>
            )}
          </div>

          {/* FOOTER */}
          <div className="p-2 border-t text-right bg-white">
            <button
              type="button"
              className="text-green-700"
              onClick={() => setOpen(false)}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
